'use client';

import type { MouseEvent } from 'react';
import { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { cn } from '@savvy-kit/lib/utils';
import { Check, Copy } from 'lucide-react';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';

type CopyAccountNumberButtonProps = {
  readonly accountNumber: string | null | undefined;
  readonly className?: string;
};

export function CopyAccountNumberButton({ accountNumber, className }: CopyAccountNumberButtonProps) {
  const { copied, copy } = useCopyToClipboard();
  const [isTooltipOpen, setIsTooltipOpen] = useState(Boolean(copied));

  if (!accountNumber) return null;

  const last4 = accountNumber.slice(-4);
  const ariaLabel = last4 ? `Copy account number ending in ${last4}` : 'Copy account number';

  const handleCopy = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    void copy(accountNumber);
  };

  return (
    <>
      <Tooltip open={copied || isTooltipOpen} onOpenChange={setIsTooltipOpen}>
        <TooltipTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={ariaLabel}
              onClick={handleCopy}
              className={cn('text-secondary', className)}
            />
          }
        >
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
        </TooltipTrigger>
        <TooltipContent>{copied ? 'Copied' : 'Copy account number'}</TooltipContent>
      </Tooltip>
      <span aria-live="polite" className="sr-only">
        {copied ? 'Account number copied to clipboard' : ''}
      </span>
    </>
  );
}
